import { MapPin, Clock, AlertCircle, Truck, CheckCircle, Phone, Mail, Navigation } from "lucide-react";
import { motion } from "framer-motion";

const ShippingPolicy = () => {
  const deliveryRules = [
    { icon: <MapPin className="w-5 h-5" />, title: "Exact Location Required", description: "Customers must select an exact delivery location at the time of booking." },
    { icon: <CheckCircle className="w-5 h-5" />, title: "Specified Location Only", description: "Bikes will only be delivered to the specified location mentioned in the booking." },
    { icon: <Clock className="w-5 h-5" />, title: "Timely Delivery", description: "Our delivery personnel will reach the designated location at the scheduled time." },
    { icon: <AlertCircle className="w-5 h-5" />, title: "Customer Presence", description: "Customers must be present at the delivery location to receive the bike." },
    { icon: <Navigation className="w-5 h-5" />, title: "Service Area", description: "Delivery service is available only within Pune and Ratnagiri city limits." },
    { icon: <AlertCircle className="w-5 h-5" />, title: "No Location Changes", description: "Bikes will not be delivered to unspecified or alternative locations after booking confirmation." },
    { icon: <CheckCircle className="w-5 h-5" />, title: "Safe Location", description: "Ensure the delivery location is accessible and safe for our personnel." },
    { icon: <Clock className="w-5 h-5" />, title: "Punctuality Matters", description: "Any delays caused by the customer at the delivery point may incur additional charges." },
  ];

  const deliverySteps = [
    { step: "1", title: "Book & Select Location", description: "Choose your exact delivery location during the booking process with complete address details.", icon: "📍" },
    { step: "2", title: "Confirmation", description: "Receive booking confirmation with scheduled delivery time and delivery personnel contact details.", icon: "✅" },
    { step: "3", title: "Preparation", description: "Our team prepares your bike, conducts safety checks, and dispatches it at the scheduled time.", icon: "🔧" },
    { step: "4", title: "Delivery", description: "Delivery personnel arrives at your location. You must be present to receive and inspect the bike.", icon: "🏍️" },
  ];

  const serviceAreas = [
    {
      city: "Pune", coverage: "Complete City Coverage",
      zones: ["Chakan", "Hinjewadi", "Kharadi", "Hadapsar", "Baner", "Wakad", "Pimpri-Chinchwad"], icon: "🏙️",
    },
    {
      city: "Ratnagiri", coverage: "Complete City Coverage",
      zones: ["City Center", "Rajapur", "Lanja", "Mirjole", "Pawas", "Mandangad"], icon: "🌊",
    },
  ];

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
  const cardItem = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <div className="relative bg-[#111111] border-b border-white/[0.08] py-16 sm:py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`, backgroundSize: "50px 50px" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[120px] opacity-5 pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Truck className="w-7 h-7 text-[#FF6B35]" />
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">Delivery</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Shipping Policy</h1>
          <p className="text-base sm:text-lg text-gray-400">Convenient doorstep delivery service for your two-wheeler rental</p>
        </motion.div>
      </div>

      {/* Notice */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-start gap-4 p-5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl">
          <Truck className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-bold text-white mb-1">Hassle-Free Bike Delivery</h3>
            <p className="text-sm text-gray-400 leading-relaxed">OkMechanic offers convenient doorstep delivery within Pune and Ratnagiri. Simply specify your location during booking, and we'll bring your chosen two-wheeler right to your doorstep at your scheduled time.</p>
          </div>
        </div>
      </div>

      {/* Delivery Rules */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-2">Delivery Terms & Rules</h2>
        <p className="text-center text-gray-500 text-sm mb-6">Important guidelines for our doorstep delivery service</p>
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {deliveryRules.map((rule, i) => (
            <motion.div key={i} variants={cardItem}
              className="bg-[#111111] border border-white/[0.08] hover:border-[#FF6B35]/20 rounded-2xl p-4 transition-colors">
              <div className="w-9 h-9 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center mb-3 text-[#FF6B35]">
                {rule.icon}
              </div>
              <h3 className="text-sm font-bold text-white mb-2">{rule.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{rule.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Delivery Steps */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-8">How Delivery Works</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {deliverySteps.map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl p-5 text-center">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-2xl flex items-center justify-center mx-auto mb-3 text-white font-black text-lg shadow-lg shadow-[#FF6B35]/20">
                {step.step}
              </div>
              <div className="text-2xl mb-2">{step.icon}</div>
              <h3 className="font-bold text-white text-sm mb-2">{step.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Service Areas */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-6">Service Areas</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {serviceAreas.map((area, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-[#111111] border border-white/[0.08] hover:border-[#FF6B35]/20 rounded-2xl p-5 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{area.icon}</span>
                <div>
                  <h3 className="font-bold text-white">{area.city}</h3>
                  <p className="text-xs text-[#FF6B35] font-medium">{area.coverage}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {area.zones.map((zone, j) => (
                  <span key={j} className="px-2 py-1 bg-white/[0.04] border border-white/[0.06] text-gray-400 rounded-lg text-xs">{zone}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">
        <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white text-center mb-6">Questions About Delivery?</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            <a href="tel:9921426002" className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors">
              <Phone className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
              <div><div className="text-xs text-gray-500">Call Us</div><div className="font-semibold text-sm">9921426002</div></div>
            </a>
            <a href="mailto:vegobike00@gmail.com" className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors">
              <Mail className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
              <div><div className="text-xs text-gray-500">Email Us</div><div className="font-semibold text-sm break-all">vegobike00@gmail.com</div></div>
            </a>
          </div>
        </div>
        <p className="text-center text-xs text-gray-600 mt-6">Last Updated: October 2025 · © 2025 OkMechanic. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ShippingPolicy;
